import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { ScanResults, CategoryResult, RiskLevel } from '@wrm/shared';
import styles from './FullResultsView.module.css';

interface FullResultsViewProps {
    results: ScanResults;
    onScanAnother: () => void;
    tier?: string;
}

const riskLevelLabels: Record<RiskLevel, string> = {
    minimal: 'Low Priority',
    low: 'Monitor',
    moderate: 'Review Recommended',
    high: 'Attention Needed',
};

const EnterpriseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);

function CategoryCard({ category }: { category: CategoryResult }) {
    return (
        <motion.div
            className={styles.category}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className={styles.categoryHeader}>
                <h3 className={styles.categoryName}>
                    {category.name.charAt(0).toUpperCase() + category.name.slice(1)}
                </h3>
                <div className={styles.categoryScore}>
                    <span className={styles.categoryScoreValue}>{category.score}</span>
                    <span className={styles.categoryScoreMax}>/100</span>
                </div>
            </div>
            <p className={styles.categoryWeight}>Weight: {Math.round(category.weight * 100)}%</p>

            <ul className={styles.findings}>
                {category.findings.map((finding) => (
                    <li key={finding.id} className={styles.finding}>
                        <span className={`${styles.findingStatus} ${styles[finding.status]}`}>
                            {finding.status === 'pass' ? '✓' : finding.status === 'fail' ? '✗' : '?'}
                        </span>
                        <div className={styles.findingContent}>
                            <p className={styles.findingTitle}>{finding.title}</p>
                            <p className={styles.findingDescription}>{finding.description}</p>
                            {finding.status !== 'pass' && finding.recommendation && (
                                <p className={styles.findingRecommendation}>
                                    Recommendation: {finding.recommendation}
                                </p>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </motion.div>
    );
}

export function FullResultsView({ results, onScanAnother, tier = 'free' }: FullResultsViewProps) {
    const tierLabels: Record<string, string> = {
        free: 'Free',
        starter: 'Starter',
        professional: 'Professional',
        enterprise: 'Enterprise',
    };

    const tierColors: Record<string, string> = {
        free: '#71717a',
        starter: '#3b82f6',
        professional: '#8b5cf6',
        enterprise: '#10b981',
    };

    return (
        <section className={styles.results}>
            <div className={styles.container}>
                {/* Header */}
                <motion.div
                    className={styles.header}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className={styles.headerBadge} style={{ background: tierColors[tier] || tierColors.free }}>
                        <EnterpriseIcon />
                        Full Results • {tierLabels[tier] || 'Free'}
                    </div>
                    <h2 className={styles.title}>Risk Assessment Results</h2>
                    <p className={styles.url}>{results.url}</p>
                </motion.div>

                {/* Score Card */}
                <motion.div
                    className={styles.scoreCard}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    <div className={styles.scoreWrapper}>
                        <span className={styles.scoreValue}>{results.overallScore}</span>
                        <span className={styles.scoreMax}>/100</span>
                    </div>
                    <p className={styles.scoreLabel}>Overall Risk Score</p>
                    <span className={`risk-badge risk-${results.riskLevel}`}>
                        {riskLevelLabels[results.riskLevel]}
                    </span>
                </motion.div>

                {/* All Categories - Full Access */}
                <div className={styles.categories}>
                    {results.categories.map((category) => (
                        <CategoryCard key={category.name} category={category} />
                    ))}
                </div>

                {/* Actions */}
                <motion.div
                    className={styles.actions}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <button onClick={onScanAnother} className="btn btn-secondary">
                        Scan Another Website
                    </button>
                    <Link to="/dashboard" className="btn btn-primary">
                        Go to Dashboard
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}
